/**
 * Sending state of the native forms that leave the page, opted in with `data-busy` (the contact forms). Shopify
 * first runs its invisible hCaptcha, then posts and redirects: a few seconds with no feedback. As soon as the form
 * is sent it gets `aria-busy="true"` (spinner on its button, contact.css) and further submits are dropped, so a
 * second click cannot send the message twice. The state ends after `TIMEOUT` ms (a closed captcha challenge never
 * leaves the page) or when the page comes back from the back/forward cache.
 * Listeners sit on the document, in the capture phase: they run before the form's own (Shopify's hCaptcha), and the
 * forms need no wrapper element (they are grid items).
 */

export const TIMEOUT = 10_000;

/** @type {WeakMap<HTMLFormElement, ReturnType<typeof setTimeout>>} */
const timers = new WeakMap();

/**
 * @param {HTMLFormElement} form
 * @param {boolean} busy
 */
export function setBusy(form, busy) {
  clearTimeout(timers.get(form));
  timers.delete(form);
  if (!busy) {
    form.removeAttribute('aria-busy');
    return;
  }
  form.setAttribute('aria-busy', 'true');
  timers.set(
    form,
    setTimeout(() => setBusy(form, false), TIMEOUT),
  );
}

/** @param {SubmitEvent} event */
function onSubmit(event) {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.hasAttribute('data-busy')) return;
  if (form.getAttribute('aria-busy') === 'true') {
    event.preventDefault();
    event.stopPropagation();
    return;
  }
  setBusy(form, true);
}

document.addEventListener('submit', onSubmit, { capture: true });
window.addEventListener('pageshow', () => {
  for (const form of document.querySelectorAll('form[data-busy][aria-busy]')) {
    if (form instanceof HTMLFormElement) setBusy(form, false);
  }
});
